<template>
  <div
    :class="['ask-clara-panel', { 'ask-clara-panel--expanded': expanded, 'ask-clara-panel--dragging': dragging }]"
    :style="panelStyle"
  >
    <!-- Header -->
    <div class="ask-clara-panel__header">
      <div class="ask-clara-panel__heading">
        <button
          class="ask-clara-panel__drag"
          :title="$t('adminTitles.askClara.move')"
          @mousedown.prevent="startDrag"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <circle cx="6" cy="3" r="1.3"/><circle cx="10" cy="3" r="1.3"/>
            <circle cx="6" cy="8" r="1.3"/><circle cx="10" cy="8" r="1.3"/>
            <circle cx="6" cy="13" r="1.3"/><circle cx="10" cy="13" r="1.3"/>
          </svg>
        </button>
        <div class="ask-clara-panel__title">
          {{ $t('adminTitles.askClara.newChat') }}
          <span class="ask-clara-panel__beta">{{ $t('adminTitles.askClara.beta') }}</span>
        </div>
      </div>
      <div class="ask-clara-panel__actions">
        <button class="ask-clara-panel__action" :title="$t('adminTitles.askClara.minimize')" @click="minimize">
          <img src="/img/renova-dashboard/icons/minus-big.svg" alt="minimize"/>
        </button>
        <button class="ask-clara-panel__action" :title="$t('adminTitles.askClara.expand')" @click="toggleExpand">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.5 2h4.5v4.5M6.5 14H2V9.5M14 2l-5 5M2 14l5-5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button class="ask-clara-panel__action" :title="$t('buttons.close')" @click="closeAndReset">
          <img src="/img/renova-dashboard/icons/x.svg" alt="close"/>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div ref="body" class="ask-clara-panel__body">
      <!-- Welcome / empty state -->
      <div v-if="!messages.length" class="ask-clara-panel__welcome">
        <svg class="ask-clara-panel__welcome-icon" width="40" height="40" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 2.2 14.5 7.4c.7.3.6 1.3-.1 1.5l-4.7 1.3-1.3 4.7c-.2.7-1.2.8-1.5.1L1.7 3.5C1.4 2.8 2.3 1.9 3 2.2Z" fill="currentColor"/>
        </svg>
        <h3 class="ask-clara-panel__welcome-title">{{ $t('adminTitles.askClara.welcomeTitle') }}</h3>
        <p class="ask-clara-panel__welcome-subtitle">{{ $t('adminTitles.askClara.welcomeSubtitle') }}</p>

        <!-- One group per hub so the two are visually separated on shared pages. -->
        <div
          v-for="hub in populatedHubs"
          :key="`hub-${hub}`"
          class="ask-clara-panel__hub-group"
        >
          <div v-if="populatedHubs.length > 1" class="ask-clara-panel__hub-label">
            {{ hubLabel(hub) }}
          </div>

          <!-- Today insights (empty in MVP) -->
          <div v-if="(insightsByHub[hub] || []).length" class="ask-clara-panel__today">
            <div class="ask-clara-panel__section-title">{{ $t('adminTitles.askClara.todayTitle') }}</div>
            <button
              v-for="(insight, i) in insightsByHub[hub]"
              :key="`insight-${hub}-${i}`"
              class="ask-clara-panel__insight"
              @click="submit(insight.text)"
            >
              <span class="ask-clara-panel__insight-emoji">{{ insight.emoji }}</span>
              <span>{{ insight.text }}</span>
            </button>
          </div>

          <!-- Suggestion chips -->
          <div v-if="(suggestionsByHub[hub] || []).length" class="ask-clara-panel__suggestions">
            <div class="ask-clara-panel__section-title">{{ $t('adminTitles.askClara.suggestionsTitle') }}</div>
            <button
              v-for="(suggestion, i) in suggestionsByHub[hub]"
              :key="`suggestion-${hub}-${i}`"
              class="ask-clara-panel__chip"
              @click="submit(suggestion)"
            >
              {{ suggestion }}
            </button>
          </div>
        </div>
      </div>

      <!-- Conversation -->
      <template v-else>
        <AskClaraMessage
          v-for="(message, i) in messages"
          :key="message.turnId || `msg-${i}`"
          :message="message"
          @feedback="onFeedback"
        />
        <div v-if="loading" class="ask-clara-panel__thinking">
          {{ $t('adminTitles.askClara.thinking') }}
        </div>
      </template>
    </div>

    <!-- Footer -->
    <div class="ask-clara-panel__footer">
      <div class="ask-clara-panel__input-wrap" :class="{ 'ask-clara-panel__input-wrap--multiline': multiline }">
        <textarea
          ref="input"
          v-model="draft"
          class="ask-clara-panel__input"
          rows="1"
          :placeholder="$t('adminTitles.askClara.placeholder')"
          @input="autoGrow"
          @keydown.enter.exact.prevent="submit()"
        />
        <button
          class="ask-clara-panel__send"
          :class="{ 'ask-clara-panel__send--active': draft.trim() && !loading }"
          :disabled="!draft.trim() || loading"
          @click="submit()"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 14.5v-11M4.5 8 9 3.5 13.5 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <!-- Escalation to a human: persistent (the welcome chips vanish once a chat starts,
           which is exactly when someone gives up on Clara). Expands inline so the panel
           stays self-contained; both links leave the SPA in a new tab. -->
      <div class="ask-clara-panel__support">
        <button class="ask-clara-panel__support-toggle" @click="supportOpen = !supportOpen">
          {{ $t('adminTitles.askClara.support.button') }}
        </button>
        <div v-if="supportOpen" class="ask-clara-panel__support-links">
          <a
            class="ask-clara-panel__support-link"
            :href="supportWhatsAppUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{{ $t('adminTitles.askClara.support.whatsapp') }}</span>
            <span class="ask-clara-panel__support-value">{{ supportPhone }}</span>
          </a>
          <a
            class="ask-clara-panel__support-link"
            :href="`mailto:${supportEmail}`"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{{ $t('adminTitles.askClara.support.email') }}</span>
            <span class="ask-clara-panel__support-value">{{ supportEmail }}</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AskClaraMessage from '@/views/RenovaDashboard/AskClaraParts/AskClaraMessage';
import { SUPPORT_PHONE, SUPPORT_WHATSAPP_URL, SUPPORT_EMAIL } from '@/utils/supportContacts';

// Input grows with its text up to this height, then scrolls.
const MAX_INPUT_HEIGHT = 300;

// Above this content height the textarea is treated as multiline.
const SINGLE_LINE_HEIGHT = 30;

// Persisted dragged position (cleared when the panel is closed via the × button).
const POSITION_STORAGE_KEY = 'ask_clara_panel_position';

// Panel geometry (kept in JS so all states use left/top/width/height and animate smoothly).
const PANEL_WIDTH = 440;
const PANEL_HEIGHT = 600;
const MARGIN_RIGHT = 24;
const MARGIN_BOTTOM = 56;
const SIDEBAR_WIDTH = 240;
const MOBILE_BREAKPOINT = 668;

export default {
  name: 'AskClaraPanel',
  components: {
    AskClaraMessage,
  },
  data() {
    return {
      draft: '',
      expanded: false,
      multiline: false,
      supportOpen: false,
      supportPhone: SUPPORT_PHONE,
      supportWhatsAppUrl: SUPPORT_WHATSAPP_URL,
      supportEmail: SUPPORT_EMAIL,
      dragging: false,
      dragPos: null, // { left, top } once the user has moved the panel
      winW: window.innerWidth,
      winH: window.innerHeight,
    };
  },
  computed: {
    ...mapGetters('Clara', [
      'claraMessages',
      'claraSuggestionsByHub',
      'claraInsightsByHub',
      'claraLoading',
    ]),
    messages() {
      return this.claraMessages;
    },
    suggestionsByHub() {
      return this.claraSuggestionsByHub;
    },
    insightsByHub() {
      return this.claraInsightsByHub;
    },
    loading() {
      return this.claraLoading;
    },
    // Hubs that actually have something to show, in a stable order. A hub the account
    // lacks comes back as [] from the backend and is simply skipped (no header/empty state).
    populatedHubs() {
      return ['SALES', 'SERVICE'].filter(
        (hub) => (this.suggestionsByHub[hub] || []).length || (this.insightsByHub[hub] || []).length,
      );
    },
    // Drive every state (default / dragged / expanded) through left/top/width/height
    // so transitions between them animate instead of snapping. Mobile is left to CSS.
    panelStyle() {
      if (this.winW <= MOBILE_BREAKPOINT) return {};
      let width;
      let height;
      let left;
      let top;
      if (this.expanded) {
        width = this.winW - SIDEBAR_WIDTH - MARGIN_RIGHT * 2;
        height = this.winH - MARGIN_BOTTOM - 40;
        left = this.winW - width - MARGIN_RIGHT;
        top = this.winH - height - MARGIN_BOTTOM;
      } else {
        width = PANEL_WIDTH;
        height = PANEL_HEIGHT;
        if (this.dragPos) {
          ({ left, top } = this.dragPos);
        } else {
          left = this.winW - width - MARGIN_RIGHT;
          top = this.winH - height - MARGIN_BOTTOM;
        }
      }
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        right: 'auto',
        bottom: 'auto',
        maxWidth: 'none',
        maxHeight: 'none',
      };
    },
  },
  watch: {
    messages() {
      this.scrollToBottom();
    },
    loading() {
      this.scrollToBottom();
    },
  },
  mounted() {
    this.loadPosition();
    window.addEventListener('resize', this.onResize);
    this.$nextTick(() => {
      if (this.$refs.input) this.$refs.input.focus();
      this.autoGrow();
      this.scrollToBottom();
      this.clampPosition();
    });
  },
  beforeDestroy() {
    this.stopDrag();
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    hubLabel(hub) {
      return this.$t(`adminTitles.askClara.hubLabels.${hub}`);
    },
    // Minimize keeps the saved position; closing via × clears it.
    minimize() {
      this.$store.dispatch('Clara/closePanel');
    },
    closeAndReset() {
      this.resetPosition();
      this.$store.dispatch('Clara/closeAndReset');
    },
    // Reset the dragged position so the expanded panel snaps cleanly into the viewport.
    toggleExpand() {
      this.expanded = !this.expanded;
      if (this.expanded) {
        this.resetPosition();
      }
    },
    resetPosition() {
      this.dragPos = null;
      localStorage.removeItem(POSITION_STORAGE_KEY);
    },
    onResize() {
      this.winW = window.innerWidth;
      this.winH = window.innerHeight;
    },
    loadPosition() {
      try {
        const saved = localStorage.getItem(POSITION_STORAGE_KEY);
        const parsed = saved ? JSON.parse(saved) : null;
        if (parsed && typeof parsed.left === 'number' && typeof parsed.top === 'number') {
          this.dragPos = parsed;
        }
      } catch (e) {
        this.dragPos = null;
      }
    },
    savePosition() {
      if (this.dragPos) {
        localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(this.dragPos));
      }
    },
    // Keep a restored position inside the current viewport (window may have resized).
    clampPosition() {
      if (!this.dragPos) return;
      const rect = this.$el.getBoundingClientRect();
      const margin = 8;
      const maxLeft = Math.max(window.innerWidth - rect.width - margin, margin);
      const maxTop = Math.max(window.innerHeight - rect.height - margin, margin);
      this.dragPos = {
        left: Math.min(Math.max(this.dragPos.left, margin), maxLeft),
        top: Math.min(Math.max(this.dragPos.top, margin), maxTop),
      };
      this.savePosition();
    },
    startDrag(e) {
      const rect = this.$el.getBoundingClientRect();
      this.dragOffsetX = e.clientX - rect.left;
      this.dragOffsetY = e.clientY - rect.top;
      this.dragWidth = rect.width;
      this.dragHeight = rect.height;
      this.dragging = true;
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    },
    onDrag(e) {
      const margin = 8;
      const maxLeft = Math.max(window.innerWidth - this.dragWidth - margin, margin);
      const maxTop = Math.max(window.innerHeight - this.dragHeight - margin, margin);
      const left = Math.min(Math.max(e.clientX - this.dragOffsetX, margin), maxLeft);
      const top = Math.min(Math.max(e.clientY - this.dragOffsetY, margin), maxTop);
      this.dragPos = { left, top };
    },
    stopDrag() {
      this.dragging = false;
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      this.savePosition();
    },
    async submit(text) {
      const question = (typeof text === 'string' ? text : this.draft).trim();
      if (!question || this.loading) return;
      this.draft = '';
      this.$nextTick(this.autoGrow);
      // Optimistic user bubble + typing indicator.
      this.$store.commit('Clara/ADD_MESSAGE', {
        turnId: null,
        role: 'USER',
        content: question,
        sourcesUsed: [],
        feedbackRating: null,
        createdAt: new Date().toISOString(),
      });
      this.$store.commit('Clara/SET_LOADING', true);
      try {
        await this.$store.dispatch('Clara/ask', question);
      } catch (error) {
        const message = error.response?.data?.message || this.$t('adminTitles.askClara.error');
        this.$store.commit('Clara/ADD_MESSAGE', {
          turnId: null,
          role: 'ASSISTANT',
          content: message,
          sourcesUsed: [],
          feedbackRating: null,
          createdAt: new Date().toISOString(),
        });
      } finally {
        this.$store.commit('Clara/SET_LOADING', false);
      }
    },
    async onFeedback({ turnId, rating, comment }) {
      if (!turnId) return;
      try {
        await this.$store.dispatch('Clara/sendFeedback', { turnId, rating, comment });
      } catch (error) {
        this.$notify.error({ title: this.$t('adminTitles.askClara.error') });
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const { body } = this.$refs;
        if (body) body.scrollTop = body.scrollHeight;
      });
    },
    // Grow the input with its content; start scrolling only past MAX_INPUT_HEIGHT (300px).
    autoGrow() {
      const el = this.$refs.input;
      if (!el) return;
      el.style.height = 'auto';
      const next = Math.min(el.scrollHeight, MAX_INPUT_HEIGHT);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
      // Single line → send button centered; multiline → anchored to the bottom.
      this.multiline = el.scrollHeight > SINGLE_LINE_HEIGHT;
    },
  },
};
</script>

<style lang="scss" scoped>
.ask-clara-panel {
  position: fixed;
  right: 24px;
  bottom: 56px; // sits just above the footer bar
  z-index: 10000000000;
  display: flex;
  flex-direction: column;
  width: 440px;
  height: 600px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 120px);
  background: #FFFFFF;
  border: 1px solid var(--solid-lavender-grey);
  border-radius: 18px;
  box-shadow: 0px 16px 48px rgba(25, 18, 69, 0.18);
  overflow: hidden;
  transition: left 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    top 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    height 0.32s cubic-bezier(0.22, 1, 0.36, 1);

  // Expanded: animate to fill the content area, stopping before the 240px sidebar.
  &--expanded {
    width: calc(100vw - 240px - 48px);
    height: calc(100vh - 96px);
  }

  &--dragging {
    transition: none;
    user-select: none;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid var(--solid-lavender-grey);
  }

  &__heading {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  &__drag {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    border-radius: 6px;
    color: var(--solid-grey, #9A99AD);
    cursor: grab;

    &:hover {
      background: var(--solid-light-purple, #F4F2FB);
      color: var(--solid-purple);
    }

    &:active {
      cursor: grabbing;
    }
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 16px;
    color: var(--solid-black-purple);
  }

  &__beta {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--solid-purple);
    border: 1px solid var(--solid-lavender-grey);
    border-radius: 10px;
    padding: 1px 8px;
  }

  &__actions {
    display: flex;
    gap: 4px;
    color: var(--solid-black-purple);
  }

  &__action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 6px;
    color: inherit;

    &:hover {
      background: var(--solid-light-purple, #F4F2FB);
    }

    img {
      width: 16px;
      height: 16px;
    }
  }

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: 18px;
  }

  &__welcome {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: var(--solid-purple);
  }

  &__welcome-icon {
    margin-bottom: 14px;
  }

  &__welcome-title {
    font-size: 18px;
    font-weight: 700;
    color: var(--solid-black-purple);
    margin: 0 0 6px;
  }

  &__welcome-subtitle {
    font-size: 14px;
    color: var(--solid-grey, #6B6B80);
    margin: 0 0 20px;
  }

  &__section-title {
    width: 100%;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--solid-grey, #6B6B80);
    margin-bottom: 10px;
  }

  &__hub-group {
    width: 100%;
    max-width: 440px;

    & + & {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid var(--solid-lavender-grey);
    }
  }

  &__hub-label {
    width: 100%;
    text-align: left;
    font-size: 13px;
    font-weight: 700;
    color: var(--solid-black-purple);
    margin-bottom: 10px;
  }

  &__today,
  &__suggestions {
    width: 100%;
    max-width: 440px;
    margin-top: 8px;
  }

  &__chip {
    display: block;
    width: 100%;
    text-align: left;
    background: #FFFFFF;
    border: 1px solid var(--solid-lavender-grey);
    border-radius: 12px;
    padding: 10px 14px;
    margin-bottom: 8px;
    font-size: 13px;
    color: var(--solid-black-purple);
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: var(--solid-purple);
      background: var(--solid-light-purple, #F4F2FB);
    }
  }

  &__insight {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    text-align: left;
    background: var(--solid-light-purple, #F4F2FB);
    border: none;
    border-radius: 12px;
    padding: 10px 14px;
    margin-bottom: 8px;
    font-size: 13px;
    color: var(--solid-black-purple);
    cursor: pointer;
  }

  &__insight-emoji {
    flex-shrink: 0;
  }

  &__thinking {
    font-size: 15px;
    font-weight: 500;
    background: linear-gradient(90deg, #b7b3c9 25%, #191245 50%, #b7b3c9 75%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: clara-shimmer 1.4s linear infinite;
  }

  &__footer {
    padding: 12px 16px 16px;
  }

  &__input-wrap {
    position: relative;
    border: 1px solid var(--solid-lavender-grey);
    border-radius: 16px;
    padding: 14px 16px;
    transition: border-color 0.15s ease;

    &:focus-within {
      border-color: var(--solid-purple);
    }
  }

  &__input {
    display: block;
    width: 100%;
    min-height: 24px;
    border: none;
    padding: 0;
    padding-right: 42px;
    font-size: 15px;
    font-family: 'Inter', sans-serif;
    line-height: 24px;
    resize: none;
    overflow-y: hidden;
    outline: none;
    background: transparent;
    color: var(--solid-black-purple);
  }

  // Centered against the single-line input; drops to the bottom once it wraps.
  &__input-wrap--multiline &__send {
    top: auto;
    bottom: 8px;
    transform: none;
  }

  &__support {
    margin-top: 8px;
  }

  &__support-toggle {
    border: none;
    background: transparent;
    padding: 2px 4px;
    font-size: 12px;
    font-family: 'Inter', sans-serif;
    color: var(--solid-grey, #6B6B80);
    cursor: pointer;
    border-radius: 6px;
    transition: color 0.15s ease;

    &:hover {
      color: var(--solid-purple);
    }
  }

  &__support-links {
    margin-top: 6px;
  }

  &__support-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border: 1px solid var(--solid-lavender-grey);
    border-radius: 12px;
    padding: 8px 14px;
    margin-bottom: 6px;
    font-size: 13px;
    color: var(--solid-black-purple);
    text-decoration: none;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: var(--solid-purple);
      background: var(--solid-light-purple, #F4F2FB);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__support-value {
    color: var(--solid-purple);
    font-weight: 600;
  }

  &__send {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border: 1px solid var(--solid-lavender-grey);
    border-radius: 50%;
    background: #FFFFFF;
    color: var(--solid-grey, #6B6B80);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &--active {
      background: var(--solid-purple);
      border-color: var(--solid-purple);
      color: #FFFFFF;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }
}

@keyframes clara-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media screen and (max-width: 668px) {
  .ask-clara-panel,
  .ask-clara-panel--expanded {
    top: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    max-width: 100vw;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }
}
</style>
